import { useState, useCallback, CSSProperties } from "react"

import { DropArea, getAcceptableFiles } from "components/Files"
import {
  deleteAgencyDocument,
  fetchAgencyDocuments,
  uploadAgencyDocument,
} from "api/agencies"
import {
  deleteCampaignDocument,
  deletePlanCreative,
  downloadDocument,
  fetchCampaignDocuments,
  fetchPlanCreatives,
  uploadCampaignDocument,
  uploadPlanCreative,
} from "api/library"
import {
  deleteClientConfigDocument,
  deleteClientDocument,
  fetchClientConfigDocuments,
  fetchClientDocuments,
  uploadClientConfigDocument,
  uploadClientDocument,
} from "api/clients"
import {
  deleteClientgroupsDocument,
  fetchClientgroupsDocuments,
  uploadClientgroupsDocument,
} from "api/clientgroups"
import {
  deleteCompanyDocument,
  fetchCompanyDocuments,
  uploadCompanyDocument,
} from "api/companies"
import {
  deleteSupplierDocument,
  fetchSupplierDocuments,
  uploadSupplierDocument,
} from "api/suppliers"
import {
  deleteSuppliergroupsDocument,
  fetchSuppliergroupsDocuments,
  uploadSuppliergroupsDocument,
} from "api/suppliergroups"
import { queryCache, useMutation, useQuery } from "react-query"

import { Document } from "models/documents"
import { DocumentUploadModal } from "components/DocumentUploadModal"
import { ReactComponent as DocumentsIcon } from "assets/icon-documents.svg"
import { EmptyState } from "components/EmptyState"
import { Item } from "components/Documents"
import { Loading } from "components/Loading"
import { PageType } from "api/types"
import { cx } from "@emotion/css"
import groupBy from "lodash/fp/groupBy"
import prop from "lodash/fp/prop"
import { styles } from "./styles"
import { useDropzone } from "react-dropzone"
import { useI18n } from "providers/I18n"

const cacheKeys = {
  campaign: "campaignDocuments",
  plan: "planCreatives",
  agency: "agencyDocuments",
  client: "clientDocuments",
  supplier: "supplierDocuments",
  suppliergroups: "suppliergroupsDocuments",
  clientgroups: "clientgroupsDocuments",
  company: "companyDocuments",
  config: "clientConfigDocuments",
}

const fetchDocument = {
  campaign: fetchCampaignDocuments,
  plan: fetchPlanCreatives,
  agency: fetchAgencyDocuments,
  client: fetchClientDocuments,
  supplier: fetchSupplierDocuments,
  suppliergroups: fetchSuppliergroupsDocuments,
  clientgroups: fetchClientgroupsDocuments,
  company: fetchCompanyDocuments,
  config: fetchClientConfigDocuments,
}

const uploadDocument = {
  campaign: uploadCampaignDocument,
  plan: uploadPlanCreative,
  agency: uploadAgencyDocument,
  client: uploadClientDocument,
  supplier: uploadSupplierDocument,
  suppliergroups: uploadSuppliergroupsDocument,
  clientgroups: uploadClientgroupsDocument,
  company: uploadCompanyDocument,
  config: uploadClientConfigDocument,
}

const deleteDocument = {
  campaign: deleteCampaignDocument,
  plan: deletePlanCreative,
  agency: deleteAgencyDocument,
  client: deleteClientDocument,
  supplier: deleteSupplierDocument,
  suppliergroups: deleteSuppliergroupsDocument,
  clientgroups: deleteClientgroupsDocument,
  company: deleteCompanyDocument,
  config: deleteClientConfigDocument,
}

export type DocumentType = { label: string; value: string }

export type DocumentsProps = {
  type: PageType
  disabled?: boolean
  ownerEntityId: string
  documentTypes?: DocumentType[]
}

export function AdminDocuments({
  type,
  disabled,
  ownerEntityId,
  documentTypes,
}: DocumentsProps) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [document, setDocument] = useState<Document | undefined>(undefined)
  const [dropStatus, setDropStatus] = useState<
    "idle" | "pending" | "success" | "rejected"
  >("idle")
  const { data: docs, isLoading: loading } = useQuery(
    [cacheKeys[type], ownerEntityId],
    fetchDocument[type],
  )

  const [upload, isLoading] = useMutation(uploadDocument[type], {
    onSuccess: () => {
      queryCache.invalidateQueries([cacheKeys[type], ownerEntityId])
      setIsModalOpen(false)
      setDocument(undefined)
    },
  })

  const { t } = useI18n()

  const [remove, { isLoading: deleting }] = useMutation(deleteDocument[type], {
    onSuccess: () => {
      queryCache.invalidateQueries([cacheKeys[type], ownerEntityId])
    },
  })

  const handleFile = useCallback(
    async (file: File) => {
      setIsModalOpen(true)
      if (document === undefined) {
        setDocument({
          file,
          name: "",
          ownerEntityId,
          dateCreated: new Date().getTime(),
        })
      } else {
        setDocument(Object.assign(document, { file }))
      }
    },
    [document, ownerEntityId],
  )

  const onDropAccepted = useCallback(
    (acceptedFiles: File[]) => {
      const [file] = acceptedFiles
      if (file) {
        setDropStatus("pending")
        handleFile(file)
          .then(() => setDropStatus("success"))
          .catch(() => setDropStatus("rejected"))
      }
    },
    [handleFile],
  )

  const dropzoneState = useDropzone({
    accept: getAcceptableFiles().accept,
    onDropAccepted,
    multiple: false,
    noClick: true,
  })

  const handleCancel = useCallback(() => {
    setIsModalOpen(false)
    setDocument(undefined)
    setDropStatus("idle")
  }, [])

  const handleDelete = useCallback(
    (id?: string) => {
      if (id !== undefined) remove(id)
    },
    [remove],
  )

  const handleSubmit = (document: Document) => {
    upload({
      ownerEntityId,
      file: document.file,
      mediaType: document.mediaType,
      description: document.description,
      documentType: document.documentType,
    })
    setDropStatus("idle")
  }

  const byDocumentType = groupBy(prop("documentType"), docs)
  const byMediaType = groupBy((d) => (d.tags ? d.tags[0] : ""), docs)
  const byCategory = type === "plan" ? byMediaType : byDocumentType

  const hasTwoColumns = Object.keys(byCategory).some(
    (category: string) => byCategory[category]?.length > 1,
  )
  const columnsNo = hasTwoColumns ? 2 : 1

  return loading ? (
    <Loading />
  ) : (
    <>
      {docs !== undefined && docs.length > 0 ? (
        <div className={cx({ [styles.overlay]: deleting })}>
          <h5 className={cx("BodyBody-1BlackBold", styles.title)}>
            {t("droparea.documents")}
          </h5>
          <div className={styles.documents}>
            {Object.keys(byCategory).map((category: string) => {
              const docsByCategory = byCategory[category].length
              const rowsNo = Math.round(docsByCategory / 2)
              return (
                <div className={styles.listWrapper} key={category}>
                  {category === "undefined" ? null : (
                    <p className={cx("BodyBody-sBlackLight", styles.listLabel)}>
                      {category}:
                    </p>
                  )}
                  <div
                    className={styles.list}
                    style={
                      {
                        "--rows": rowsNo,
                        "--columns": columnsNo,
                      } as CSSProperties
                    }
                  >
                    {byCategory[category].map((doc) => (
                      <Item
                        key={doc.id}
                        document={doc}
                        disabled={disabled}
                        onDelete={handleDelete}
                        onDownload={downloadDocument}
                        type={type}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : disabled && !isLoading ? (
        <EmptyState
          title={t("no.documents")}
          className={styles.emptyStateWidth}
          icon={<DocumentsIcon />}
        />
      ) : null}

      {!disabled && (
        <>
          <DropArea
            documentsLabel={t(`${type}.documents.description`)}
            dropzoneState={dropzoneState}
          />
          <DocumentUploadModal
            type={type}
            open={isModalOpen}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            document={document}
            documentTypes={documentTypes}
            dropStatus={dropStatus}
          />
        </>
      )}
    </>
  )
}
